import { useNavigate, Link } from 'react-router';
import { useAuth } from '../../context/AuthContext';
import { useState, useEffect } from 'react';
import { Mail, Lock, AlertCircle } from 'lucide-react';
import logoImg from '../../../assets/a50295a0af56ffb8dcd053f3d73ca9d37c948ba6.png';
import logoDarkImg from '../../../assets/ff6cec488a700597f19b04a772a6a682dadfe9e6.png';

export function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    const theme = document.documentElement.getAttribute('data-theme');
    return theme === 'dark';
  });

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const theme = document.documentElement.getAttribute('data-theme');
      setIsDark(theme === 'dark');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    setIsLoading(true);

    try {
      const success = await login(email, password);
      if (!success) {
        setError('E-mail ou senha incorretos');
        return;
      }
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('userEmail', email);
      }
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'E-mail ou senha incorretos');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{ backgroundColor: 'var(--background)' }}
    >
      <div
        className="w-full max-w-md p-8"
        style={{
          backgroundColor: 'var(--card)',
          borderRadius: 'var(--radius-card)',
          border: '1px solid',
          borderColor: 'var(--border)',
        }}
      >
        <div className="text-center mb-8">
          <img
            src={isDark ? logoDarkImg : logoImg}
            alt="SimulaDesk Logo"
            style={{
              maxHeight: '60px',
              width: 'auto',
              objectFit: 'contain',
              marginBottom: '8px',
              display: 'inline-block',
            }}
          />
          <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)' }}>
            Treinamento e auditoria de atendimento com IA
          </p>
        </div>

        {error && (
          <div
            className="flex items-center gap-3 p-4 mb-6"
            style={{
              backgroundColor: 'var(--destructive)',
              color: 'var(--destructive-foreground)',
              borderRadius: 'var(--radius)',
            }}
          >
            <AlertCircle size={20} />
            <p style={{ fontSize: 'var(--text-sm)' }}>{error}</p>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block mb-2"
              style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
            >
              E-mail
            </label>
            <div className="relative">
              <Mail
                size={18}
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--muted-foreground)',
                }}
              />
              <input
                id="email"
                type="email"
                placeholder="seu@email.com"
                className="w-full pl-10 pr-4 py-3 border"
                style={{
                  backgroundColor: 'var(--input-background)',
                  borderColor: error ? 'var(--destructive)' : 'var(--border)',
                  borderRadius: 'var(--radius-input)',
                  color: 'var(--foreground)',
                }}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label
                htmlFor="password"
                style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
              >
                Senha
              </label>
              <Link
                to="/esqueci-senha"
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--primary)',
                  textDecoration: 'none',
                }}
                className="transition-opacity hover:opacity-70"
              >
                Esqueci minha senha
              </Link>
            </div>
            <div className="relative">
              <Lock
                size={18}
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--muted-foreground)',
                }}
              />
              <input
                id="password"
                type="password"
                placeholder="••••••••"
                className="w-full pl-10 pr-4 py-3 border"
                style={{
                  backgroundColor: 'var(--input-background)',
                  borderColor: error ? 'var(--destructive)' : 'var(--border)',
                  borderRadius: 'var(--radius-input)',
                  color: 'var(--foreground)',
                }}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
              />
            </div>
          </div>

          <div className="flex items-center">
            <input
              id="rememberMe"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              style={{
                width: '16px',
                height: '16px',
                marginRight: '8px',
                cursor: 'pointer',
              }}
            />
            <label
              htmlFor="rememberMe"
              style={{ fontSize: 'var(--text-sm)', cursor: 'pointer' }}
            >
              Lembrar meu e-mail neste dispositivo
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full px-6 py-3 transition-opacity hover:opacity-90"
            style={{
              backgroundColor: 'var(--primary)',
              color: 'var(--primary-foreground)',
              borderRadius: 'var(--radius-button)',
              border: 'none',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            {isLoading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>


      </div>
    </div>
  );
}



